import { useEffect, useRef } from 'react';
import { authClient } from '@/lib/auth-client';
import { useAuthStore } from '@/stores/useAuthStore';
import { useReferralStore } from '@/stores/useReferralStore';
import { useSyncStore } from '@/stores/useSyncStore';
import { useCatalogSyncStore } from '@/stores/useCatalogSyncStore';
import { useOnboardingStore } from '@/stores/useOnboardingStore';
import { migrateLocalToCloud, migrateCatalogToCloud } from '@/stores/migrate';
import { loadCatalogOverrides, loadCustomServices } from '@/features/auth/lib/catalogOverridesLoader';
import {
  AUTH_BROADCAST_CHANNEL,
  isSelfOrigin,
  type AuthBroadcastMessage,
} from '@/features/auth/lib/authBroadcast';
import { withRetry } from '@/shared/lib/retry';
import { syncAndHydrate } from '@/features/sync';

// SAAS-02: plan sync. Fetches GET /api/user/plan after session is established and mirrors
// the server-authoritative plan into useAuthStore. The result is UX state only — never used
// for authorization (T-08-16).
// CR-01: accepts setPlanLoaded so RequireAuth can stop spinning once the fetch settles.
// On failure: calls setPlanLoaded (fail-open on client — Paywall is shown via access:'none'
// default; server requireAccess (Phase 15) is the real gate, so no unbounded access is granted).
async function fetchAndSyncPlan(
  setPlan: (plan: 'free' | 'pro', status: string | null, periodEnd: number | null) => void,
  setAccess: (access: 'trial' | 'entitled' | 'none', trialEndsAt: number | null) => void,
  setPlanLoaded: () => void,
  setRewardDays: (days: number | null) => void,
  setHasPassword: (value: boolean | null) => void,
): Promise<void> {
  try {
    const res = await fetch('/api/user/plan');
    if (!res.ok) {
      // Non-2xx response (e.g. 500, network proxy error): treat as failure.
      // CR-01: mark plan loaded so RequireAuth doesn't spin; access stays 'none' → Paywall shown.
      setPlanLoaded();
      return;
    }
    const body = (await res.json()) as {
      plan: 'free' | 'pro';
      planStatus: string | null;
      currentPeriodEnd: number | null;
      // Phase 15 fields — present on Phase 15+ server; undefined on old-server rollback
      access: 'trial' | 'entitled' | 'none';
      trialEndsAt: number | null; // Unix MILLISECONDS (entitlement.ts TRIAL_MS convention)
      // Phase 21 — onboarding_done: present on Phase 21+ server; undefined on old-server rollback
      onboardingDone?: boolean;
      // Phase 27 — present on Phase 25+ server; undefined on old-server rollback.
      // ?? null guard: missing field → null (no bonus pill shown, conservative default — T-16-03).
      rewardDaysRemaining?: number | null;
      // Phase 43 (PWD-08) — present on Phase 43+ server; undefined on old-server rollback.
      hasPassword?: boolean;
    };
    setPlan(body.plan, body.planStatus, body.currentPeriodEnd);
    // ?? guards old-server rollback: missing fields default to 'none'/null (T-16-03 — conservative
    // default shows paywall, never grants unbounded access).
    // setAccess also sets planLoaded:true (CR-01 — single state update, no extra render).
    setAccess(body.access ?? 'none', body.trialEndsAt ?? null);
    // Phase 27: mirror rewardDaysRemaining into UX store (T-08-16 — UX only, never authorization).
    // ?? null guard: old-server rollback (field absent) → null → no bonus pill shown.
    setRewardDays(body.rewardDaysRemaining ?? null);
    // Phase 43 (PWD-08): mirror hasPassword into UX store — UX only, never authorization.
    // ?? null guard: old-server rollback (field absent) → null, NOT false — an unknown
    // state must suppress the password-nudge banner, not trigger it (conservative default).
    setHasPassword(body.hasPassword ?? null);
    // Phase 21 (ONBOARD-03): cross-device suppression — if server says done, set locally.
    // Uses setFromServer which never un-sets a locally-true flag (server-false is ignored).
    useOnboardingStore.getState().setFromServer(body.onboardingDone ?? false);
  } catch {
    // Network error / JSON parse failure: fail-open — setPlanLoaded so RequireAuth stops spinning.
    // access stays 'none' → Paywall shown (conservative; server is the real gate — T-16-03).
    setPlanLoaded();
  }
}

// useAuth mirrors better-auth's session (httpOnly cookie) into the non-persisted useAuthStore.
// Guest-first invariant: this hook NEVER throws or blocks rendering.
// When there is no session (or while pending), the store stays in guestMode: true.

// WR-02 fix: module-level in-flight flag to prevent concurrent migration runs within a tab.
// The useEffect deps (session, setUser, clearUser) can fire on every re-render where
// session.user.id is truthy, but migration must run ONCE per login, not once per render.
// Reset to false in the finally block so a same-tab re-login unblocks a new run.
// NOTE: migrationInFlight is NOT reset on logout — this is intentional and safe because
// the per-userId localStorage flags (getMigratedFlag / getCatalogMigratedFlag in migrate.ts)
// are the authoritative idempotency guard. migrationInFlight only prevents concurrent
// in-tab double-runs during a single login session; it does not guard across logouts.
let migrationInFlight = false;

export function useAuth() {
  const { data: session, isPending, refetch } = authClient.useSession();
  const {
    setUser,
    clearUser,
    setPlan,
    setAuthResolved,
    setAccess,
    setPlanLoaded,
    setRewardDays,
    setHasPassword,
  } = useAuthStore();

  // WR-01 fix: guard so auth-success broadcast fires ONLY on the first setUser per userId.
  // Per the HTML spec, the self-exclusion rule applies to the sender BroadcastChannel
  // *instance*, not the browsing context — the persistent listener in Effect 2 CAN receive
  // messages from the fire-and-forget sender created in Effect 1 (different object, same tab).
  // Without this guard, every re-render that sees an authenticated session would rebroadcast,
  // causing one redundant GET /api/auth/get-session. With the guard, the broadcast fires once
  // per unique userId and the ref is cleared on logout via component unmount.
  const broadcastedForRef = useRef<string | null>(null);

  // WR-02 fix: guard so loadCatalogOverrides fires ONLY on the first render per userId.
  // visibilitychange → refetch() can return a new session object reference for the same userId
  // (React Query re-validates stale queries), causing Effect 1 to re-fire and issuing redundant
  // GET /api/catalog/overrides. The ref mirrors the broadcastedForRef pattern above.
  // Reset to null in the guest branch so a re-login on the same component instance re-triggers.
  const catalogLoadedForRef = useRef<string | null>(null);

  // IN-01 fix: guard so the attribution POST fires at most once per mounted session, per userId.
  // Without this, a ?ref= param persisting in the URL causes the attribution fetch to re-fire on
  // every tab focus / visibilitychange that triggers a session re-render. The server is idempotent
  // (ON CONFLICT DO NOTHING) so no double-reward occurs, but unnecessary requests are wasteful.
  // Mirrors the broadcastedForRef / catalogLoadedForRef pattern above.
  // Reset to null in the guest branch so a re-login on the same component instance can re-trigger.
  const attributionAttemptedForRef = useRef<string | null>(null);

  // SYNC-03/04: guard so the login sync chain fires at most once per userId per mount.
  // The session watcher re-runs on visibilitychange refetches (same pattern as
  // catalogLoadedForRef/attributionAttemptedForRef above); re-hydrating the whole
  // library on every refocus would be a needless request storm (T-04-08).
  // Reset to null in the guest branch so a re-login on the same component instance
  // re-triggers the full flush -> first-sync repair -> hydrate chain.
  const hydratedForRef = useRef<string | null>(null);

  // Session watcher: mirrors session into Zustand store; activates sync and cross-tab broadcast.
  useEffect(() => {
    if (session?.user?.id && session.session) {
      // WR-03: store only userId + expiry, not the raw session token.
      // The token is held exclusively in the httpOnly cookie — never in Zustand state.
      setUser(session.user.id, session.session.expiresAt);

      // SAAS-02: Fire-and-forget plan sync — mirrors server plan into UX state.
      // Called after setUser so the store is in authenticated state when plan arrives.
      // CR-01: passes setPlanLoaded so planLoaded flips true when fetch settles (success or fail).
      // Phase 27: passes setRewardDays to mirror rewardDaysRemaining into UX store (T-08-16).
      // Phase 43 (PWD-08): passes setHasPassword to mirror hasPassword into UX store.
      void fetchAndSyncPlan(setPlan, setAccess, setPlanLoaded, setRewardDays, setHasPassword);

      // CAT-01 (Phase 12): Fire-and-forget catalog overrides load — applies server-side
      // per-user price/name overrides to the local service store after login.
      // WR-02: gated on userId change (catalogLoadedForRef) to prevent concurrent GETs on
      // visibilitychange refetches — mirrors the broadcastedForRef pattern above.
      // Errors are swallowed via .catch — guest-first invariant preserved.
      if (session.user.id !== catalogLoadedForRef.current) {
        catalogLoadedForRef.current = session.user.id;
        // HARDEN-03: wrap in withRetry — 5xx/timeout retried (exp backoff 500ms×2^i);
        // 4xx fails fast; null on final failure (graceful degradation, catalog stays at
        // current state). withRetry never rejects; .catch is belt-and-suspenders guard.
        void withRetry(() => loadCatalogOverrides()).catch(console.warn);
        // CAT-03 (Plan 13-05): fire-and-forget custom services load — applies server-side
        // per-user custom services to the local store after login.
        // Same guard (catalogLoadedForRef) prevents redundant GETs on visibilitychange (WR-02).
        // Race with migrateCatalogToCloud is safe: applyCustomServices merges local-only
        // customs (Pitfall 5 — offline-created customs are preserved regardless of order).
        // HARDEN-03: wrapped in withRetry for same retry/degrade contract as above.
        void withRetry(() => loadCustomServices()).catch(console.warn);
      }

      // SYNC-01/02/03/04: Activate project sync after login. The chain is
      // flush() -> first-sync repair -> hydrate, sequential by construction
      // (see src/features/sync/lib/hydrate.ts) — never Promise.all, so
      // un-synced local history uploads (and any pre-Phase-35 stripped row is
      // repaired from the still-intact local copy) before server state is
      // read down into the library. Runs only in the session-present branch
      // so it never fires while isPending (no userId yet -> server would
      // return 401). Fire-and-forget from this effect's perspective: one
      // .catch, never awaited by the effect body.
      // T-04-08: guarded by hydratedForRef (mirrors catalogLoadedForRef) so the
      // session watcher's visibilitychange re-runs don't re-hydrate the whole
      // library on every refocus.
      if (session.user.id !== hydratedForRef.current) {
        hydratedForRef.current = session.user.id;
        syncAndHydrate().catch(console.warn);
      }
      // HARDEN-02: Flush catalog offline queue at login (adjacent to project sync flush).
      // Covers login-time reconnect scenario (SC-1). Silent background operation — no status badge.
      // Left untouched: separate queue, separate store, no reordering, no merging.
      useCatalogSyncStore.getState().flush().catch(console.warn);

      // AUTH-02: Broadcast auth-success to all other tabs so they can re-fetch the session
      // without requiring a page reload. Fire-and-forget: create → postMessage → close.
      // WR-01: gated on userId change — prevents self-notification on every re-render.
      // The listener in Effect 2 (same tab, different BroadcastChannel object) is eligible
      // to receive from this sender; the ref prevents the redundant extra refetch cycle.
      if (session.user.id !== broadcastedForRef.current) {
        broadcastedForRef.current = session.user.id;
        const bc = new BroadcastChannel('estimatix-auth');
        bc.postMessage({ type: 'auth-success' });
        bc.close();
      }

      // CLOUD-06 / MIG-01: Fire-and-forget guest→cloud migrations on first login.
      // - Runs AFTER auth flow completes (setUser called above first).
      // - NEVER awaited — does not block auth or rendering (T-04-16 mitigation).
      // - Both functions have their own idempotent flag guards preventing repeat runs.
      // - Boot order is safe: App.tsx resolves useProjectStore.persist.rehydrate() (SAFE-05,
      //   which runs migrateProjectStore as its persist `migrate` option) before setReady(true),
      //   so nameIsManual is already backfilled before login fires.
      // - WR-02: guarded by migrationInFlight to prevent concurrent runs on re-renders.
      // - Plan 13-05 (MIG-01): migrateCatalogToCloud runs in parallel with migrateLocalToCloud
      //   (Promise.all). Both are independent one-shot first-login ops. The shared
      //   migrationInFlight flag gates both so only one pair of migrations runs per login.
      if (!migrationInFlight) {
        migrationInFlight = true;
        Promise.all([
          migrateLocalToCloud(session.user.id),
          migrateCatalogToCloud(session.user.id),
        ])
          .catch(console.warn)
          .finally(() => {
            migrationInFlight = false;
          });
      }

      // Phase 25: Attribution — fire POST /api/referral/attribute if a referral code is pending.
      // Primary path: sessionStorage (set by /invite/:code route on mount).
      // Secondary path: URL param ?ref=CODE (catches the case where sessionStorage was cleared
      //   between tab navigation — e.g., user opens magic link in a different browser session).
      // IN-01 fix: gated on attributionAttemptedForRef — fires at most once per userId per mount.
      const storedCode = sessionStorage.getItem('referral_code');
      const urlCode = new URLSearchParams(window.location.search).get('ref');
      const refCode = storedCode ?? urlCode;

      if (refCode && session.user.id !== attributionAttemptedForRef.current) {
        attributionAttemptedForRef.current = session.user.id;
        void (async () => {
          try {
            const res = await fetch('/api/referral/attribute', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ referralCode: refCode }),
            });
            if (res.ok) {
              // Clear regardless of alreadyAttributed — code is consumed on any 2xx.
              // ON CONFLICT DO NOTHING on server means retries are safe; sessionStorage
              // removal is the client-side idempotency guard (Pitfall 4).
              sessionStorage.removeItem('referral_code');
              useReferralStore.getState().clearReferral();
            } else {
              // Non-2xx: leave sessionStorage intact — next session load will retry.
              // Reset the guard so the next session load can retry.
              attributionAttemptedForRef.current = null;
            }
          } catch {
            // Network error: leave sessionStorage intact for retry.
            // Reset the guard so the next session load can retry.
            attributionAttemptedForRef.current = null;
            console.warn('[phase-25] attribution fetch failed');
          }
        })();
      }
    } else if (!isPending) {
      // No session and not in a loading state — restore guest mode
      clearUser();
      // WR-02: reset catalogLoadedForRef so a re-login on the same component instance
      // re-triggers the catalog load (new user may have different overrides).
      catalogLoadedForRef.current = null;
      // IN-01: reset attributionAttemptedForRef so a re-login can re-trigger attribution.
      attributionAttemptedForRef.current = null;
      // SYNC-03/04: reset hydratedForRef so a re-login on the same component
      // instance re-triggers the full flush -> first-sync repair -> hydrate chain.
      hydratedForRef.current = null;
      // Phase 12 (Pitfall 3): signal that the first session-check is complete and user is
      // confirmed as a guest. This flips authLoading→false so ServiceMigration's guard
      // can safely run nameKey self-healing (only for guests; blocked for logged-in users).
      // Intentionally separate from clearUser — clearUser owns AUTH-04 queue-clearing (OQ-6).
      setAuthResolved();
    }
    // If isPending is true and there's no session yet, do nothing — wait for resolution
    // This prevents a flash of guestMode during the initial session fetch
  }, [
    session,
    isPending,
    setUser,
    clearUser,
    setPlan,
    setAuthResolved,
    setAccess,
    setPlanLoaded,
    setRewardDays,
    setHasPassword,
  ]);

  // HARDEN-02: Mid-session reconnect — flush catalog offline queue when browser goes online.
  // Covers SC-2 (user edits catalog offline, then reconnects without re-logging in).
  // isReallyOnline() probe inside flush() guards against captive-portal false-positives (T-28-02-04).
  // Guard for non-browser environments (SSR / Worker runtimes).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => {
      useCatalogSyncStore.getState().flush().catch(console.warn);
    };
    window.addEventListener('online', handler);
    return () => {
      window.removeEventListener('online', handler);
    };
  }, []);

  // SYNC-04: Mid-session reconnect — flush the PROJECT queue when browser goes online.
  // Covers the user who edits offline and reconnects without re-logging in. Structurally
  // identical to the catalog-queue listener above: same non-browser guard, same
  // addEventListener/removeEventListener cleanup pair, empty dependency array.
  // flush()'s own isReallyOnline() HEAD probe is what guards against captive-portal
  // false positives (T-04-09) — no extra guard is needed here.
  // Hydration deliberately does NOT run on reconnect: reading the whole library down on
  // every network blip is not what the reconnect case needs; flush() is what unblocks
  // the user's pending edits.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => {
      useSyncStore.getState().flush().catch(console.warn);
    };
    window.addEventListener('online', handler);
    return () => {
      window.removeEventListener('online', handler);
    };
  }, []);

  // AUTH-02: Cross-tab session listener + mobile visibilitychange fallback.
  // When the magic link opens in a new tab and the user clicks it, that tab fires
  // the auth-success broadcast (above). This effect catches it in all OTHER tabs.
  // The visibilitychange fallback handles the mobile Safari scenario where BroadcastChannel
  // may not fire reliably when the app regains focus after being backgrounded.
  useEffect(() => {
    const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);

    const handleMessage = (e: MessageEvent<AuthBroadcastMessage>) => {
      if (e.data.type === 'auth-success') {
        void refetch();
      } else if (e.data.type === 'session-revoked') {
        // T-43-21: self-origin guard. BroadcastChannel delivers to every OTHER channel
        // object in the same tab (see the sender comment above), and this hook is mounted
        // twice (AuthInit, MagicLinkForm) — a consume-once flag would be eaten by whichever
        // listener runs first. isSelfOrigin is stateless and keyed on a per-tab id instead,
        // independent of listener count (43-04 flagged_assumptions item 3).
        if (isSelfOrigin(e.data.origin)) return;
        // T-43-22 / PWD-06: set the flag ONLY — never call refetch()/clearUser()
        // synchronously from inside a message handler, so it cannot race an in-flight
        // write. SessionRevokedOverlay reacts to the flag at render time.
        useAuthStore.getState().setSessionRevoked(true);
      } else {
        // Exhaustiveness guard: npx tsc --noEmit rejects this line (and therefore the
        // whole file) if AuthBroadcastMessage ever grows a third member that isn't
        // handled by one of the branches above.
        const unhandled: never = e.data;
        void unhandled;
      }
    };
    channel.addEventListener('message', handleMessage);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refetch();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refetch]);

  const signIn = async (email: string) => {
    // Phase 25: Secondary mechanism — thread callbackURL with the referral code so it survives
    // the magic-link email→click→redirect chain in cases where sessionStorage is cleared between
    // /invite/:code mount and magic-link click (e.g., user opens link in a different browser).
    // sessionStorage is PRIMARY; callbackURL is defense-in-depth (real-world email gateways
    // can rewrite/strip query params, making sessionStorage more reliable for same-browser flows).
    const refCode = sessionStorage.getItem('referral_code');
    return authClient.signIn.magicLink({
      email,
      callbackURL: refCode ? `/?ref=${encodeURIComponent(refCode)}` : '/',
    });
  };

  return { signIn, session, isPending };
}
